import React, { useState, useEffect } from 'react';
import { storage } from '../firebase';
import { ref, getDownloadURL, uploadBytes } from 'firebase/storage';
import './SignsList.css';

const SignsList = () => {
  const [signs, setSigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingSign, setEditingSign] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    fetchSigns();
  }, []);

  const fetchSigns = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the correct storage path
      const signsRef = ref(storage, 'gs://elite-ios-a6a68.appspot.com/signs/all-signs.json');
      
      try {
        const url = await getDownloadURL(signsRef);
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!Array.isArray(data)) {
          throw new Error('Invalid data format: expected an array');
        }
        
        setSigns(data);
        console.log('Successfully loaded signs:', data.length);
      } catch (error) {
        console.error('Error fetching signs:', error);
        
        if (error.code === 'storage/object-not-found') {
          setError('Signs file not found. Please ensure the file exists in Firebase Storage.');
        } else if (error.code === 'storage/unauthorized') {
          setError('You do not have permission to access the signs file.');
        } else if (error.message.includes('network')) {
          setError('Network error. Please check your internet connection and try again.');
        } else {
          setError(`Failed to fetch signs data: ${error.message}`);
        }
      }
    } catch (error) {
      console.error('Error in fetchSigns:', error);
      setError('An unexpected error occurred while fetching signs data.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (sign) => {
    setEditingSign({ ...sign });
  };

  const handleSave = async () => {
    try {
      setError(null);
      const updatedSigns = signs.map(sign => 
        sign.id === editingSign.id ? editingSign : sign
      );
      
      // Use the correct storage path
      const signsRef = ref(storage, 'gs://elite-ios-a6a68.appspot.com/signs/all-signs.json');
      const jsonString = JSON.stringify(updatedSigns, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const file = new File([blob], 'all-signs.json', { type: 'application/json' });
      
      await uploadBytes(signsRef, file);
      setSigns(updatedSigns);
      setEditingSign(null);
      console.log('Successfully saved changes');
    } catch (error) {
      console.error('Error saving sign:', error);
      if (error.code === 'storage/unauthorized') {
        setError('You do not have permission to save changes.');
      } else if (error.message.includes('network')) {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        setError(`Failed to save changes: ${error.message}`);
      }
    }
  };

  const handleCancel = () => {
    setEditingSign(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setEditingSign(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value
    }));
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedSigns = signs
    .filter(sign => 
      Object.values(sign).some(value => 
        String(value).toLowerCase().includes(searchQuery.toLowerCase())
      )
    )
    .sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (typeof aValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return sortDirection === 'asc' 
        ? aValue - bValue
        : bValue - aValue;
    });

  if (loading) {
    return <div className="signs-list loading">Loading signs...</div>;
  }

  if (error) {
    return (
      <div className="signs-list error">
        <p>{error}</p>
        <button onClick={fetchSigns}>Retry</button>
      </div>
    );
  }

  return (
    <div className="signs-list">
      <div className="signs-header">
        <h2>Signs Management</h2>
        <div className="signs-controls">
          <input
            type="text"
            placeholder="Search signs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <button onClick={fetchSigns} className="refresh-button">
            Refresh
          </button>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th onClick={() => handleSort('id')}>
                ID {sortField === 'id' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('asset_name')}>
                Asset Name {sortField === 'asset_name' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('status')}>
                Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('city')}>
                City {sortField === 'city' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('state')}>
                State {sortField === 'state' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedSigns.map(sign => (
              <tr key={sign.id}>
                {editingSign && editingSign.id === sign.id ? (
                  <>
                    <td>
                      <input
                        type="number"
                        name="id"
                        value={editingSign.id}
                        onChange={handleInputChange}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="asset_name"
                        value={editingSign.asset_name}
                        onChange={handleInputChange}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="status"
                        value={editingSign.status}
                        onChange={handleInputChange}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="city"
                        value={editingSign.city}
                        onChange={handleInputChange}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="state"
                        value={editingSign.state}
                        onChange={handleInputChange}
                      />
                    </td>
                    <td>
                      <button onClick={handleSave} className="save-button">Save</button>
                      <button onClick={handleCancel} className="cancel-button">Cancel</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{sign.id}</td>
                    <td>{sign.asset_name}</td>
                    <td>{sign.status}</td>
                    <td>{sign.city}</td>
                    <td>{sign.state}</td>
                    <td>
                      <button onClick={() => handleEdit(sign)} className="edit-button">
                        Edit
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SignsList; 